import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Grid, Chip, CircularProgress, Divider, useMediaQuery, useTheme, FormControl, InputLabel, Select, MenuItem, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,  } from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Receipt as InvoiceIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import client from '../../api/client';
import CustomTextField from '../../components/CustomTextField';
import { toPersianDigits } from '../../utils/numberConverter';

const themeColors = {
  dark: '#1a1a2e',
  gold: '#d4af37',
  goldLight: '#f4e4bc',
};

const emptyItem = {
  item_id: '',
  item_name: '',
  item_type: '',
  description: '',
  quantity: '',
  unit_type: 'meter',
  unit_price: '',
};

const CreateInvoice = () => {
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState(true);
  const [items, setItems] = useState([]);
  const [groupedItems, setGroupedItems] = useState({});
  const [invoiceItems, setInvoiceItems] = useState([{ ...emptyItem }]);
  const [order, setOrder] = useState(null);

  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchItems();
    fetchOrder();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await client.get('/orders/items/grouped/');
      setGroupedItems(response.data);
      
      // فلت کردن همه آیتم‌ها
      const allItems = [
        ...(response.data.fabric?.items || []),
        ...(response.data.sewing?.items || []),
        ...(response.data.accessory?.items || []),
        ...(response.data.service?.items || []),
      ];
      setItems(allItems);
    } catch (error) {
      console.error('Error fetching items:', error);
    } finally {
      setLoadingItems(false);
    }
  };

  const fetchOrder = async () => {
    try {
      const response = await client.get(`/orders/${id}/`);
      setOrder(response.data);
    } catch (error) {
      toast.error('خطا در دریافت اطلاعات سفارش');
      handleClose();
    }
  };

  const handleClose = () => {
    setOpen(false);
    navigate(`/shop/orders/${id}`);
  };

  const handleAddItem = () => {
    setInvoiceItems([...invoiceItems, { ...emptyItem }]);
  };

  const handleRemoveItem = (index) => {
    if (invoiceItems.length === 1) {
      toast.warning('حداقل یک قلم الزامی است');
      return;
    }
    const newItems = invoiceItems.filter((_, i) => i !== index);
    setInvoiceItems(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...invoiceItems];
    newItems[index][field] = value;
    
    // اگر کالا انتخاب شد
    if (field === 'item_id') {
      const selectedItem = items.find(i => i.id === value);
      if (selectedItem) {
        newItems[index].item_name = selectedItem.name;
        newItems[index].item_type = selectedItem.item_type;
        // تعیین واحد بر اساس نوع
        newItems[index].unit_type = selectedItem.item_type === 'service' ? 'count' : 'meter';
      }
    }
    
    setInvoiceItems(newItems);
  };

  const calculateItemTotal = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.unit_price) || 0;
    return qty * price;
  };

  const calculateTotal = () => {
    return invoiceItems.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('fa-IR').format(num || 0);
  };

  

  const handleSubmit = async () => {
    // اعتبارسنجی
    for (let i = 0; i < invoiceItems.length; i++) {
      const item = invoiceItems[i];
      if (!item.item_id) {
        toast.error(`کالا در ردیف ${i + 1} انتخاب نشده`);
        return;
      }
      if (!item.quantity || parseFloat(item.quantity) <= 0) {
        toast.error(`تعداد/متر در ردیف ${i + 1} الزامی است`);
        return;
      }
      if (!item.unit_price || parseFloat(item.unit_price) <= 0) {
        toast.error(`قیمت واحد در ردیف ${i + 1} الزامی است`);
        return;
      }
    }

    setLoading(true);
    try {
      await client.post('/orders/invoices/create/', {
        order_id: parseInt(id),
        items: invoiceItems.map(item => ({
          item_id: item.item_id,
          description: item.description,
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price),
        }))
      });
      toast.success('فاکتور با موفقیت ایجاد شد');
      navigate(`/shop/orders/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'خطا در ایجاد فاکتور');
    } finally {
      setLoading(false);
    }
  };

  // گروه‌بندی آیتم‌ها برای Select
  const renderItemOptions = () => {
    const options = [];
    
    if (groupedItems.fabric?.items?.length > 0) {
      options.push(
        <MenuItem key="fabric-header" disabled sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>
          پارچه
        </MenuItem>
      );
      groupedItems.fabric.items.forEach(item => {
        options.push(
          <MenuItem key={item.id} value={item.id}>
            {item.name}
          </MenuItem>
        );
      });
    }
    
    if (groupedItems.sewing?.items?.length > 0) {
      options.push(
        <MenuItem key="sewing-header" disabled sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>
          دوخت
        </MenuItem>
      );
      groupedItems.sewing.items.forEach(item => {
        options.push(
          <MenuItem key={item.id} value={item.id}>
            {item.name}
          </MenuItem>
        );
      });
    }
    
    if (groupedItems.accessory?.items?.length > 0) {
      options.push(
        <MenuItem key="accessory-header" disabled sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>
          لوازم
        </MenuItem>
      );
      groupedItems.accessory.items.forEach(item => {
        options.push(
          <MenuItem key={item.id} value={item.id}>
            {item.name}
          </MenuItem>
        );
      });
    }
    
    if (groupedItems.service?.items?.length > 0) {
      options.push(
        <MenuItem key="service-header" disabled sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>
          خدمات
        </MenuItem>
      );
      groupedItems.service.items.forEach(item => {
        options.push(
          <MenuItem key={item.id} value={item.id}>
            {item.name}
          </MenuItem>
        );
      });
    }
    
    return options;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          minHeight: isMobile ? '100%' : 'auto',
          maxHeight: '95vh',
        }
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: themeColors.dark,
          color: themeColors.gold,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InvoiceIcon />
          <Typography variant="h6" fontWeight="bold">
            ایجاد فاکتور
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: themeColors.gold }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {/* اطلاعات سفارش */}
        {order && (
          <Card sx={{ mb: 2, bgcolor: 'grey.100' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">شماره سفارش</Typography>
                  <Typography fontWeight="bold">{order.order_number}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">مشتری</Typography>
                  <Typography fontWeight="bold">{order.customer_name}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">موبایل</Typography>
                  <Typography dir="ltr" sx={{ textAlign: 'right' }}>
                    {toPersianDigits(order.customer_mobile)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">اندازه‌گیری‌ها</Typography>
                  <Typography>{toPersianDigits(order.measurements_count)}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {loadingItems ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: themeColors.gold }} />
          </Box>
        ) : (
          <>
            {/* جدول اقلام */}
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              اقلام فاکتور
            </Typography>

            {invoiceItems.map((item, index) => (
              <Card key={index} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ py: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      ردیف {toPersianDigits((index + 1).toString())}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveItem(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <Grid container spacing={2}>
                    {/* کالا */}
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>کالا *</InputLabel>
                        <Select
                          value={item.item_id}
                          label="کالا *"
                          onChange={(e) => handleItemChange(index, 'item_id', e.target.value)}
                        >
                          {renderItemOptions()}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* توضیحات */}
                    <Grid item xs={12} sm={4}>
                      <CustomTextField                         fullWidth
                        size="small"
                        label="توضیحات (کد، رنگ و...)"
                        placeholder="مثال: کد ۱۰۱۲"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      />
                    </Grid>

                    {/* نمایش نام کالا + توضیحات */}
                    {item.item_name && (
                      <Grid item xs={12}>
                        <Chip 
                          label={`${item.item_name}${item.description ? ` - ${item.description}` : ''}`}
                          sx={{ bgcolor: themeColors.goldLight }}
                        />
                      </Grid>
                    )}

                    {/* تعداد/متر */}
                    <Grid item xs={6} sm={2}>
                      <CustomTextField                         fullWidth
                        size="small"
                        label={item.unit_type === 'count' ? 'تعداد *' : 'متر *'}
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {item.unit_type === 'count' ? 'بار' : 'متر'}
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    {/* قیمت واحد */}
                    <Grid item xs={6} sm={3}>
                      <CustomTextField                         fullWidth
                        size="small"
                        label="قیمت واحد *"
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">ریال</InputAdornment>,
                        }}
                      />
                    </Grid>

                    {/* قیمت کل */}
                    <Grid item xs={12} sm={3}>
                      <Box sx={{ 
                        p: 1, 
                        bgcolor: 'grey.100', 
                        borderRadius: 1,
                        textAlign: 'center',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}>
                        <Typography variant="caption" color="text.secondary">قیمت کل</Typography>
                        <Typography fontWeight="bold" color={themeColors.gold}>
                          {formatNumber(calculateItemTotal(item))} ریال
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}

            {/* دکمه اضافه کردن */}
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddItem}
              sx={{
                py: 1.5,
                borderStyle: 'dashed',
                borderWidth: 2,
                mb: 2,
              }}
            >
              افزودن قلم جدید
            </Button>

            {/* جمع کل */}
            <Card sx={{ bgcolor: themeColors.dark, color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">جمع کل فاکتور:</Typography>
                  <Typography variant="h5" fontWeight="bold" color={themeColors.gold}>
                    {formatNumber(calculateTotal())} ریال
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={handleClose}>
          انصراف
        </Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            flexGrow: 1,
            bgcolor: themeColors.gold,
            color: themeColors.dark,
            '&:hover': { bgcolor: themeColors.goldLight },
          }}
        >
          ثبت فاکتور
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateInvoice;
